import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { type ASTNode, print } from 'graphql';
import gql from 'graphql-tag';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AudienceOptionType } from '@/api/audience';
import type { VendorAndChannelType } from '@/api/vendor';
import { type QueryStateFilter } from '@/components/InstantSearch';
import { isFilterRange } from '@/components/InstantSearch/util/filter-util';
import type SolAPIError from '@/error/SolAPIError';
import { useSolQuery } from '@/hooks/useSolQuery';
import { useNotification } from '@/providers/Notification';

import { fetcherSol } from '../sol-fetcher';
import {
  type CSVJobBegin,
  type DisplayNamesGQLResponse,
  type DownloadCSVBeginGQLResponse,
  type DownloadCSVStatusGQLResponse,
  type OptionType,
  type ROMSByObjectIdsGQLResponse,
  type TrafficDetailKpisGQLResponse,
  type TrafficVisitBreakdownGQLResponse,
  type TrafficVisitTrendByVendorGQLResponse,
  type TrafficVisitTrendGQLResponse,
  type TrafficVisitTrendWithPreviousPeriodGQLResponse,
  type VisitTrafficType,
} from './commonType';

export const useDisplayName = (id?: string | false | null) => {
  const { displayNames, isLoading, error } = useDisplayNames(id ? [id] : undefined);

  return {
    displayName: displayNames?.[0]?.name,
    isLoading,
    error,
  };
};

const getKnownDisplayNames = (ids?: string[]) => {
  const displayNamesById: Record<string, string> = {
    CLOSED_WON: t`Closed Won`,
    CLOSED_LOST: t`Closed Lost`,
    PIPELINE: t`Open`,
    '': t`(Blanks)`,
    true: t`True`,
    false: t`False`,
  };

  const knownDisplayNames: OptionType[] = [];

  const unknownIds = ids?.filter((id) => {
    const name = displayNamesById[id];
    if (name) {
      knownDisplayNames.push({ id, name });
      return false;
    }
    return true;
  });

  return {
    unknownIds,
    knownDisplayNames,
  };
};

export const useDisplayNames = (ids?: string[]) => {
  const { unknownIds, knownDisplayNames } = getKnownDisplayNames(ids);
  const { data, isLoading, error } = useSolQuery<DisplayNamesGQLResponse>({
    query:
      !!unknownIds?.length &&
      gql`
        query DisplayNames($ids: [String!]!) {
          displayNames {
            get(ids: $ids) {
              id
              name
            }
          }
        }
      `,
    variables: {
      ids: unknownIds,
    },
  });

  const displayNames = useMemo(() => {
    return [...knownDisplayNames, ...(data?.displayNames.get ?? [])];
  }, [knownDisplayNames, data]);

  return {
    displayNames,
    isLoading,
    error,
  };
};

export const useFilterNames = (filters: QueryStateFilter[]) => {
  getKnownDisplayNames();

  const filterIds = filters.reduce((memo: string[], filter) => {
    if (isFilterRange(filter)) {
      return memo;
    }

    const ids = Array.isArray(filter.operand) ? filter.operand : [filter.operand];

    // If a filter field is an id, fetch the display name for it.
    if (
      !filter.field.endsWith('Ids') &&
      !filter.field.endsWith('Id') &&
      !filter.field.endsWith('.id') &&
      !filter.field.startsWith('vendorId') &&
      !filter.field.startsWith('campaignTypeId') &&
      !filter.field.startsWith('campaignId')
    ) {
      // If we're dealing with enums that we already know the display names for, we can just
      // return the ids.
      const { knownDisplayNames } = getKnownDisplayNames(ids);
      return memo.concat(knownDisplayNames.map(({ id }) => id));
    }

    return memo.concat(ids);
  }, []);

  const { displayNames, isLoading, error } = useDisplayNames(filterIds);

  const filtersWithNames = useMemo(() => {
    if (!displayNames) {
      return filters;
    }

    const idLookup = displayNames.reduce(
      (memo: Record<string, string | undefined>, { id, name }) => {
        memo[id] = name;
        return memo;
      },
      {},
    );

    // replace filter with id operands with name operands
    return filters.map((filter) => {
      if (isFilterRange(filter)) {
        return filter;
      }

      return {
        ...filter,
        operand: filter.operand.map((id) => idLookup[id] || id),
      };
    });
  }, [displayNames, filters]);

  return {
    filtersWithNames,
    isLoading,
    error,
  };
};

type DownloadCSVInput = {
  filename: string;
  query: ASTNode;
  variables: object;
};

const fetchBeginDownloadCSV = async ({ filename, query, variables }: DownloadCSVInput) => {
  const payload = JSON.stringify({
    query: print(query),
    variables,
  });

  const todayStr = dayjs().format('MM_DD_YYYY');

  const response = await fetcherSol<
    { filename: string; query: string },
    DownloadCSVBeginGQLResponse
  >({
    query: gql`
      query BeginCSV($query: String!, $filename: String) {
        csv {
          begin(query: $query, filename: $filename) {
            id
            url
            status
          }
        }
      }
    `,
    variables: {
      query: payload,
      filename: `${filename}_${todayStr}`,
    },
  });

  if (response.csv.begin.status === 'error') {
    throw new Error();
  }

  return response.csv.begin;
};

const useDownloadCSVStatus = (id?: string, pollingInterval = 3000) => {
  const canExecuteQuery = !!id;

  const { data, error, isLoading } = useSolQuery<DownloadCSVStatusGQLResponse>(
    {
      query:
        canExecuteQuery &&
        gql`
          query DownloadCSVStatus($id: String!) {
            csv {
              status(id: $id) {
                id
                status
                url
              }
            }
          }
        `,
      variables: {
        id,
      },
    },
    {
      refetchInterval: canExecuteQuery ? pollingInterval : 0,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    },
  );

  const downloadStatus = data?.csv.status;

  if (downloadStatus?.status === 'error') {
    return {
      downloadStatus: undefined,
      error: new Error(),
      isLoading: false,
    };
  }

  return {
    downloadStatus,
    error,
    isLoading,
  };
};

const downloadFile = (url: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.click();
  link.remove();
};

export const useDownloadCSV = ({ filename, query, variables }: DownloadCSVInput) => {
  const { pushNotification, removeAllNotifications } = useNotification();
  const [beginResponse, setBeginResponse] = useState<CSVJobBegin>();
  const [isLoading, setIsLoading] = useState(false);
  const [beginError, setBeginError] = useState<SolAPIError>();

  const { downloadStatus, error } = useDownloadCSVStatus(
    beginError ? undefined : beginResponse?.id,
  );

  const dispatchError = useCallback(() => {
    pushNotification({
      type: 'error',
      message: t`Failed to download file. Please try again or contact support if the issue persists.`,
    });
  }, [pushNotification]);

  useEffect(() => {
    // Reset begin response if download status finished processing (whether successful or not)
    if (downloadStatus?.status === 'completed' && isLoading) {
      if (beginResponse?.url) {
        downloadFile(beginResponse.url);
      } else {
        pushNotification({
          type: 'error',
          message: t`Failed to download file. Please try again or contact support if the issue persists.`,
        });
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBeginResponse(undefined);
      setIsLoading(false);

      pushNotification({
        type: 'success',
        message: t`Download complete!`,
      });
    }
  }, [downloadStatus?.status, isLoading, pushNotification, beginResponse]);

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBeginResponse(undefined);
      setIsLoading(false);
      dispatchError();
    }
  }, [error, dispatchError]);

  return {
    downloadStatus: downloadStatus ?? beginResponse,
    isLoading,
    error: error ?? beginError,
    beginDownload: async () => {
      removeAllNotifications();
      setBeginResponse(undefined);
      setBeginError(undefined);
      setIsLoading(true);

      try {
        const response = await fetchBeginDownloadCSV({ filename, query, variables });

        setBeginResponse(response);
        setBeginError(undefined);
      } catch (e) {
        dispatchError();
        setBeginResponse(undefined);
        setBeginError(e as SolAPIError);
        setIsLoading(false);
      }
    },
    cancelDownload: async () => {
      setBeginResponse(undefined);
      setBeginError(undefined);
      setIsLoading(false);
    },
  };
};

export const useTrafficVisitTrend = (
  startDate?: string,
  endDate?: string,
  {
    audienceId,
    vendorId,
    campaignIds = [],
  }: { audienceId?: string; vendorId?: string; campaignIds?: string[] } = {},
) => {
  const canExecuteQuery = startDate?.length && endDate?.length;

  const { data, isLoading, error } = useSolQuery<TrafficVisitTrendGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query TrafficVisitTrend2(
          $startDate: DateTime!
          $endDate: DateTime!
          $adIds: [String]
          $audienceIds: [String]
        ) {
          visualization {
            trafficVisitTrend2(
              startDate: $startDate
              endDate: $endDate
              adIds: $adIds
              audienceIds: $audienceIds
            ) {
              sundayOfWeek
              allVisits: total
              botVisits: bot
              otherVisits: other
              targetVisits: target
              unresolvedVisits: unresolved
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds: audienceId ? [audienceId] : [],
      adIds: campaignIds,
      vendorId,
    },
  });

  return {
    trafficTrendData: data?.visualization.trafficVisitTrend2,
    isLoading,
    error,
  };
};

export const useTrafficVisitTrendWithPreviousPeriod = (startDate?: string, endDate?: string) => {
  const canExecuteQuery = startDate?.length && endDate?.length;
  let previousStartDate;
  let previousEndDate;

  if (startDate && endDate) {
    const spanInDays = dayjs(endDate).diff(startDate, 'day');
    previousEndDate = dayjs(startDate).subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
    previousStartDate = dayjs(previousEndDate).subtract(spanInDays, 'day').format('YYYY-MM-DD');
  }

  const { data, isLoading, error } = useSolQuery<TrafficVisitTrendWithPreviousPeriodGQLResponse>({
    query:
      !!canExecuteQuery &&
      gql`
        query TrafficVisitTrend2WithPreviousPeriod(
          $startDate: DateTime!
          $endDate: DateTime!
          $previousStartDate: DateTime!
          $previousEndDate: DateTime!
        ) {
          visualization {
            currentPeriod: trafficVisitTrend2(startDate: $startDate, endDate: $endDate) {
              sundayOfWeek
              allVisits: total
              botVisits: bot
              otherVisits: other
              targetVisits: target
              unresolvedVisits: unresolved
            }
            previousPeriod: trafficVisitTrend2(
              startDate: $previousStartDate
              endDate: $previousEndDate
            ) {
              sundayOfWeek
              allVisits: total
              botVisits: bot
              otherVisits: other
              targetVisits: target
              unresolvedVisits: unresolved
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      previousStartDate: previousStartDate + 'T00:00:00Z',
      previousEndDate: previousEndDate + 'T23:59:59Z',
    },
  });

  return {
    currentPeriodData: data?.visualization.currentPeriod,
    previousPeriodData: data?.visualization.previousPeriod,
    isLoading,
    error,
  };
};

export const useTrafficVisitTrendByVendor = (
  startDate?: string,
  endDate?: string,
  {
    companyId,
    opportunityId,
    audience,
  }: { companyId?: string; opportunityId?: string; audience?: AudienceOptionType } = {},
) => {
  const canExecuteQuery = !!(companyId || opportunityId) && !!startDate && !!endDate;
  const { data, isLoading, error } = useSolQuery<TrafficVisitTrendByVendorGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query TrafficVisitTrend3(
          $endDate: DateTime!
          $companyIds: [String]
          $startDate: DateTime!
          $audienceIds: [String]
          $opportunityIds: [String]
        ) {
          visualization {
            trafficVisitTrend3(
              endDate: $endDate
              companyIds: $companyIds
              startDate: $startDate
              audienceIds: $audienceIds
              opportunityIds: $opportunityIds
            ) {
              sundayOfWeek
              allVisits: total
              vendor {
                id
                name
                color
                channel {
                  id
                  name
                }
              }
            }
          }
        }
      `,
    variables: {
      endDate: endDate + 'T23:59:59Z',
      startDate: startDate + 'T00:00:00Z',
      audienceIds: audience?.id ? [audience.id] : undefined,
      companyIds: companyId ? [companyId] : undefined,
      opportunityIds: opportunityId ? [opportunityId] : undefined,
    },
  });

  const visitTrend = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return data.visualization.trafficVisitTrend3.reduce<
      Record<
        string,
        {
          vendor: VendorAndChannelType;
          data: { sundayOfWeek: string; runningTotalVisits: number; visits: number }[];
        }
      >
    >((memo, curr) => {
      const vendorId = curr.vendor.id;

      if (!memo[vendorId]) {
        memo[vendorId] = {
          vendor: curr.vendor,
          data: [],
        };
      }
      const memoVendor = memo[vendorId];

      const lastMemoPoint = memoVendor.data.length
        ? memoVendor.data[memoVendor.data.length - 1]
        : undefined;

      const runningTotalVisits = lastMemoPoint?.runningTotalVisits ?? 0;

      memo[vendorId].data.push({
        sundayOfWeek: curr.sundayOfWeek,
        runningTotalVisits: runningTotalVisits + curr.allVisits,
        visits: curr.allVisits,
      });
      return memo;
    }, {});
  }, [data]);

  return {
    visitTrend: visitTrend ? Object.values(visitTrend) : undefined,
    isLoading,
    error,
  };
};

export const useTrafficDetailKpis = ({
  startDate,
  endDate,
  audienceIds,
  vendorIds,
  campaignIds,
  opportunityIds,
  companyIds,
}: {
  startDate?: string;
  endDate?: string;
  audienceIds?: string | string[];
  vendorIds?: string[];
  campaignIds?: string[];
  opportunityIds?: string[];
  companyIds?: string[];
}) => {
  const canExecuteQuery = !!(startDate && endDate);

  const { data, isLoading, error } = useSolQuery<TrafficDetailKpisGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query TrafficKpi2(
          $startDate: DateTime!
          $endDate: DateTime!
          $audienceIds: [String]
          $vendorIds: [String]
          $campaignIds: [String]
          $opportunityIds: [String]
          $companyIds: [String]
        ) {
          visualization {
            trafficKpi2(
              startDate: $startDate
              endDate: $endDate
              audienceIds: $audienceIds
              vendorIds: $vendorIds
              campaignIds: $campaignIds
              opportunityIds: $opportunityIds
              companyIds: $companyIds
            ) {
              closedWonInfluenced
              closedWonOpportunities
              engagedCompanies
              engagedCompaniesLastPeriod
              engagedCompaniesPercentChange
              percentEfficiency
              pipelineInfluenced
              pipelineOpportunities
              roms
              spend
              visits
              visitsLastPeriod
              visitsPercentChange
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds,
      vendorIds,
      campaignIds,
      opportunityIds,
      companyIds,
    },
  });

  return {
    kpiData: data?.visualization.trafficKpi2,
    isLoading,
    error,
  };
};

export const useROMSByObjectIds = ({
  startDate,
  endDate,
  audienceIds,
  vendorIds,
  campaignIds,
}: {
  startDate?: string;
  endDate?: string;
  audienceIds?: string | string[];
  vendorIds?: string[];
  campaignIds?: string[];
}) => {
  const canExecuteQuery = !!(startDate && endDate);

  const { data, isLoading, error } = useSolQuery<ROMSByObjectIdsGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query CampaignRoms(
          $startDate: DateTime!
          $endDate: DateTime!
          $audienceIds: [String]
          $vendorIds: [String]
          $adIds: [String]
        ) {
          visualization {
            campaignRoms(
              startDate: $startDate
              endDate: $endDate
              audienceIds: $audienceIds
              vendorIds: $vendorIds
              adIds: $adIds
            ) {
              channelAverageRoms
              industryAverageRoms
              roms
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      audienceIds,
      vendorIds,
      adIds: campaignIds,
    },
  });

  return {
    romsData: data?.visualization.campaignRoms,
    isLoading,
    error,
  };
};

export const useTrafficVisitBreakdown = ({
  startDate,
  endDate,
  audienceIds,
  channelId,
  vendorId,
  campaignIds,
  companyId,
  groupBy = 'traffic_type',
}: {
  startDate?: string;
  endDate?: string;
  audienceIds?: string[];
  channelId?: string;
  vendorId?: string;
  campaignIds?: string[];
  companyId?: string;
  groupBy?: 'traffic_type' | 'revrange' | 'sector';
}) => {
  const canExecuteQuery = !!(startDate && endDate);

  let groupingField: 'channel' | 'vendor' | 'campaign' | 'traffic_type' | 'revrange' | 'sector';

  if (groupBy === 'traffic_type') {
    if (channelId) {
      groupingField = 'channel';
    } else if (vendorId) {
      groupingField = 'vendor';
    } else if (campaignIds) {
      groupingField = 'campaign';
    } else {
      groupingField = 'traffic_type';
    }
  } else {
    groupingField = groupBy;
  }

  const { data, isLoading, error } = useSolQuery<TrafficVisitBreakdownGQLResponse>({
    query:
      canExecuteQuery &&
      gql`
        query TrafficVisitBreakdown2(
          $startDate: DateTime!
          $endDate: DateTime!
          $groupingField: TrafficVisitBreakdownGrouping2!
          $companyId: String
          $trafficType: TrafficVisitBreakdownTrafficTypes
          $channelId: String
          $vendorId: String
          $audienceIds: [String]
          $campaignIds: [String]
        ) {
          visualization {
            trafficVisitBreakdown2(
              startDate: $startDate
              endDate: $endDate
              groupingField: $groupingField
              companyId: $companyId
              trafficType: $trafficType
              channelId: $channelId
              vendorId: $vendorId
              audienceIds: $audienceIds
              campaignIds: $campaignIds
            ) {
              allVisits
              audienceVisits
              botVisits
              otherNonAudienceVisits
              otherNonTamVisits
              tamVisits
              unresolvedVisits
            }
          }
        }
      `,
    variables: {
      startDate: startDate + 'T00:00:00Z',
      endDate: endDate + 'T23:59:59Z',
      groupingField,
      vendorId,
      audienceIds,
      channelId,
      campaignIds,
      companyId,
    },
  });

  const trafficVisitBreakdown = useMemo(() => {
    const trafficVisitBreakdown = data?.visualization.trafficVisitBreakdown2;

    if (!trafficVisitBreakdown) {
      return undefined;
    }

    return trafficVisitBreakdown.map<VisitTrafficType>((item) => ({
      allVisits: item.allVisits,
      botVisits: item.botVisits,
      otherVisits: audienceIds ? item.otherNonAudienceVisits : item.otherNonTamVisits,
      targetVisits: audienceIds ? item.audienceVisits : item.tamVisits,
      unresolvedVisits: item.unresolvedVisits,
    }));
  }, [data, audienceIds]);

  return {
    trafficVisitBreakdown,
    isLoading,
    error,
  };
};
